import React from 'react';
import styled from 'styled-components';
import { Col, Row } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Card from '../Card';
import { almostBlack, boxShadow, green } from '../../styles/constants';
import RangeChart from '../RangeChart';
import InfoToolTip from '../InfoToolTip';

const Small = styled.span`
  font-size: 0.9rem;
`;

const Img = styled.img`
  object-fit: cover;
  height: 200px;
  width: 100%;
  border-radius: 5px 5px 0 0;
`;

const HeaderRow = styled(Row)`
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 1rem;
  margin-bottom: 1rem;
`;

const Title = styled.span`
  font-size: 1rem;
  font-weight: 600;
  display: block;
`;

const Subtitle = styled(Small)`
  font-weight: 400;
  display: block;
  margin-top: -2px;
  opacity: 0.6;
`;

const WillChangeCol = styled(Col)`
  will-change: opacity;
`;

const Property = styled(Small)`
  font-weight: 600;
  display: block;
`;

const ImpactProperty = styled(Property)`
  color: ${green};
`;

const AddOrRemoveText = styled(Small)`
  font-weight: 600;
  color: ${(props) => (props.$selected ? almostBlack : green)}
`;

const StyledCard = styled(Card)`
  padding: 0;
`;

const CardContent = styled.div`
  padding: 0.5rem 1rem 0.5rem;
`;

const StyledFontAwesomeIcon = styled(FontAwesomeIcon)`
  color: ${(props) => (props.$selected ? almostBlack : green)}
`;

const ImageRow = styled(Row)`
  flex: 1
`;

const AddOrRemove = styled.div`
  position: absolute;
  padding: 1rem;
  background: white;
  border-radius: 5px 0 5px;
  box-shadow: ${boxShadow};
`;

const Copyright = styled.span`
  position: absolute;
  bottom: 0.5rem;
  left: 2rem;
  font-size: 0.8rem;
  font-weight: 600;
  opacity: 0.8;
`;

const SubjectCard = ({
  title,
  subtitle,
  imagePath,
  copyrightText,
  ranges,
  rangesHeading,
  handleClick,
  isSelected,
  impact,
  propertiesToList,
  ...props
}) => (
  <WillChangeCol
    onClick={handleClick}
    {...props}
  >
    <StyledCard
      className="mb-4"
      $selected={isSelected}
      selectable
    >
      <ImageRow>
        <Col>
          <AddOrRemove>
            <StyledFontAwesomeIcon
              icon={isSelected ? 'check-circle' : 'plus-circle'}
              $selected={isSelected}
              className="mr-2"
              size="lg"
            />
            <AddOrRemoveText selected={isSelected}>{isSelected ? 'Geselecteerd' : 'Selecteren'}</AddOrRemoveText>
          </AddOrRemove>
          <Img fluid src={`${process.env.REACT_APP_API_URL}${imagePath}`} />
          {copyrightText && <Copyright>{copyrightText}</Copyright>}
        </Col>
      </ImageRow>
      <CardContent>
        <HeaderRow>
          <Col>
            <Title>{title}</Title>
            <Subtitle>{subtitle}</Subtitle>
          </Col>
        </HeaderRow>
        {
          impact && (
            <Row className="mt-2">
              <Col>
                <ImpactProperty>
                  {impact}
                </ImpactProperty>
              </Col>
            </Row>
          )
        }
        <Row>
          <Col>
            {
                propertiesToList.map((propertyToList) => (
                  <div className="mt-2" key={propertyToList.key}>
                    <Property>
                      {propertyToList.key}
                      :
                      {
                        propertyToList.help && (
                          <InfoToolTip text={propertyToList.help} />
                        )
                      }
                    </Property>
                    <Small>{propertyToList.value}</Small>
                  </div>
                ))
              }
          </Col>
        </Row>
        {
          (ranges && ranges.length > 0) && (
            <Row className="mt-2">
              <Col>
                <Property className="d-block mb-3">{rangesHeading}</Property>
                <RangeChart ranges={ranges} />
              </Col>
            </Row>
          )
        }
      </CardContent>
    </StyledCard>
  </WillChangeCol>
);

SubjectCard.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  handleClick: PropTypes.func.isRequired,
  rangesHeading: PropTypes.string.isRequired,
  impact: PropTypes.string,
  propertiesToList: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ).isRequired,
  ranges: PropTypes.arrayOf(
    PropTypes.shape({
      start: PropTypes.string.isRequired,
      end: PropTypes.string.isRequired,
    }),
  ).isRequired,
  imagePath: PropTypes.string.isRequired,
  copyrightText: PropTypes.string,
  isSelected: PropTypes.bool,
};

SubjectCard.defaultProps = {
  isSelected: false,
  impact: null,
  copyrightText: null,
};

export default SubjectCard;
